import React, { useState, useEffect } from 'react';
import { collection, onSnapshot, getDocs, getDoc, doc, setDoc, addDoc, serverTimestamp, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import LoadingIndicator from '../common/LoadingIndicator';
import { useAuth } from '../contexts/AuthContext';
import ReactDOM from 'react-dom';
import CourseIntroduction from './CourseIntroduction';

function CoursePage() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [certificationCourses, setCertificationCourses] = useState({});
  const [recommendedCourses, setRecommendedCourses] = useState({});
  const [activeCategory, setActiveCategory] = useState({
    certification: null,
    recommended: null,
  });
  const [loading, setLoading] = useState(true);
  const [enrolledCourses, setEnrolledCourses] = useState([]);
  const [activeTab, setActiveTab] = useState(0);

  const allCategories = [
    'Arts and Humanities', 'Biology and Health', 'Business', 'Information Technology', 'Language Learning',
    'Personal Development', 'Social Sciences', 'Teaching and Academics'
  ];

  useEffect(() => {
    setLoading(true);
    const unsubscribe = onSnapshot(collection(db, 'courses'), (snapshot) => {
      const allCourses = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));

      const normalizeCourseType = (value) => {
        if (value === true) return 'certification';
        if (value === false || value == null) return 'non-certification';
        let t = String(value).toLowerCase().trim();
        // normalize whitespace/underscores and collapse
        t = t.replace(/[_\s]+/g, '-');
        // accept common typo 'certifcation' and any string starting with 'cert'
        if (t === 'certification' || t === 'certifcation' || t.startsWith('cert')) {
          return 'certification';
        }
        return 'non-certification';
      };

      const getStatusValue = (status) => {
        switch (status) {
          case 'coming_soon': return 2;
          case 'available':
          default:
            return 1;
        }
      };

      // Normalize type; default anything not explicitly 'certification' to recommended
      const normalized = allCourses.map(c => ({
        ...c,
        type: normalizeCourseType(c.type),
      }));

      const sortByStatus = (a, b) => {
        const statusA = a.status || 'available';
        const statusB = b.status || 'available';
        return getStatusValue(statusA) - getStatusValue(statusB);
      };

      const groupByCategory = (courses) => courses.reduce((groups, course) => {
        const category = course.category || 'Uncategorized';
        if (!groups[category]) groups[category] = [];
        groups[category].push(course);
        return groups;
      }, {});

      const certCourses = normalized.filter(c => c.type === 'certification').sort(sortByStatus);
      const recCourses = normalized.filter(c => c.type !== 'certification').sort(sortByStatus);

      setCertificationCourses(groupByCategory(certCourses));
      setRecommendedCourses(groupByCategory(recCourses));
      setLoading(false);
    }, (error) => {
      console.error('Error fetching courses:', error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!user) return;
    
    const fetchEnrolledCourses = async () => {
      try {
        const progressRef = collection(db, 'users', user.uid, 'courseProgress');
        const progressSnap = await getDocs(progressRef);
        const enrolledIds = progressSnap.docs.map(doc => doc.id);
        
        const coursesData = await Promise.all(
          enrolledIds.map(async (courseId) => {
            const courseDoc = await getDoc(doc(db, 'courses', courseId));
            const progressDoc = await getDoc(doc(db, 'users', user.uid, 'courseProgress', courseId));
            return {
              ...courseDoc.data(),
              id: courseId,
              progress: progressDoc.data()?.progress || 0
            };
          })
        );
        
        setEnrolledCourses(coursesData);
      } catch (error) {
        console.error('Error fetching enrolled courses:', error);
      }
    };

    fetchEnrolledCourses();
  }, [user]);

  const CourseCard = ({ course }) => {
    const [showDetails, setShowDetails] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [isEnrolled, setIsEnrolled] = useState(false);
    const [courseProgress, setCourseProgress] = useState(0);
    const [loading, setLoading] = useState(true);
    const [hasFailed, setHasFailed] = useState(false);
    const [showRetakeModal, setShowRetakeModal] = useState(false);

    useEffect(() => {
      const fetchEnrollmentAndProgress = async () => {
        setLoading(true);
        try {
          const completedRef = doc(db, 'users', user.uid, 'completedSubCourses', course.id);
          const completedDoc = await getDoc(completedRef);
          
          if (completedDoc.exists()) {
            setIsEnrolled(true);
            const completedData = completedDoc.data().completed || {};
            
            // Safely compute total sub-courses even if modules/subCourses are not arrays
            const modulesArray = Array.isArray(course.modules)
              ? course.modules
              : (course.modules && typeof course.modules === 'object')
                ? Object.values(course.modules)
                : [];

            let totalSubCourses = 0;
            modulesArray.forEach((module) => {
              const subCoursesArray = Array.isArray(module?.subCourses)
                ? module.subCourses
                : (module?.subCourses && typeof module.subCourses === 'object')
                  ? Object.values(module.subCourses)
                  : [];
              totalSubCourses += subCoursesArray.length;
            });

            // Deduplicate completions in case user retook sub-courses (keys like module_subCourse[_attempt])
            const uniqueCompletionKeys = new Set();
            Object.keys(completedData).forEach((key) => {
              if (typeof key === 'string' && key.length > 0) {
                const [moduleIdPart, subCourseIdPart] = key.split('_');
                if (moduleIdPart && subCourseIdPart) {
                  uniqueCompletionKeys.add(`${moduleIdPart}_${subCourseIdPart}`);
                }
              }
            });
            const completedCount = uniqueCompletionKeys.size;
            let calculatedProgress = totalSubCourses > 0 ? (completedCount / totalSubCourses) * 100 : 0;
            // Clamp to [0, 100]
            calculatedProgress = Math.max(0, Math.min(100, calculatedProgress));

            setCourseProgress(calculatedProgress);

            const progressRef = doc(db, 'users', user.uid, 'courseProgress', course.id);
            await setDoc(progressRef, {
              progress: calculatedProgress,
              enrolledAt: completedDoc.data().firstCompletedAt || new Date().toISOString(),
              lastUpdated: new Date().toISOString()
            }, { merge: true });
          } else {
            const progressRef = doc(db, 'users', user.uid, 'courseProgress', course.id);
            const progressDoc = await getDoc(progressRef);
            
            if (progressDoc.exists()) {
              setIsEnrolled(true);
              const storedProgress = progressDoc.data().progress || 0;
              setCourseProgress(Math.max(0, Math.min(100, storedProgress)));
            }
          }
          // Determine failed status: any subcourse result exhausted attempts and < 75
          try {
            const resultsRef = collection(db, 'users', user.uid, 'examResults');
            const resultsSnap = await getDocs(resultsRef);
            let failed = false;
            resultsSnap.forEach((r) => {
              const d = r.data();
              if (d && d.courseId === course.id) {
                const attempts = Number(d.attempts || 0);
                const score = Number(d.score || 0);
                if (attempts >= 3 && score < 75) {
                  failed = true;
                }
              }
            });
            setHasFailed(failed);
          } catch (e) {
            console.warn('Failed to evaluate failed status:', e);
          }

        } catch (error) {
          console.error('Error fetching enrollment and progress:', error);
        }
        setLoading(false);
      };

      if (user && course.id) {
        fetchEnrollmentAndProgress();
      }
    }, [course.id, user]);

    const handleCertificateDownload = () => {
      if (courseProgress === 100) {
        navigate(`/courses/${course.id}/certificate`);
      } else {
        alert('Course must be fully completed to view certificate');
      }
    };

    const toggleDetails = () => {
      if (courseProgress === 100) {
        handleCertificateDownload();
      } else {
        setShowDetails(!showDetails);
      }
    };

    const handleEnroll = async () => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        setLoading(true);
        
        await setDoc(doc(db, 'users', user.uid, 'courseProgress', course.id), {
          progress: 0,
          enrolledAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        }, { merge: true });
        
        await setDoc(doc(db, 'users', user.uid, 'completedSubCourses', course.id), {
          completed: {},
          firstCompletedAt: new Date().toISOString()
        }, { merge: true });

        setIsEnrolled(true);
        setCourseProgress(0);
        setShowModal(false);
        navigate(`/courses/${course.id}`);

        // --- Add notification for course enrollment ---
        try {
           await addDoc(collection(db, 'notifications'), {
             userId: user.uid,
             title: 'Welcome to Your New Course!',
             message: `You have successfully enrolled in the course "**${course.title}**". Get started on your learning journey!`, // Use Markdown
             type: 'course_enrollment',
             read: false,
             dismissed: false,
             createdAt: serverTimestamp()
           });
           console.log('Course enrollment notification sent.');
        } catch (notificationError) {
            console.error('Error sending course enrollment notification:', notificationError);
        }
        // --- End notification logic ---

      } catch (error) {
        console.error('Error enrolling in course:', error);
        alert('Failed to enroll in course. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    const handleLearnClick = () => {
      if (!user) {
        navigate('/login');
        return;
      }

      if (isEnrolled && !hasFailed) {
        navigate(`/courses/${course.id}`);
      } else {
        if (hasFailed) setShowRetakeModal(true);
        else setShowModal(true);
      }
    };
    const handleRetake = async () => {
      if (!user) {
        navigate('/login');
        return;
      }
      try {
        setLoading(true);
        // Reset progress
        await setDoc(doc(db, 'users', user.uid, 'courseProgress', course.id), {
          progress: 0,
          enrolledAt: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        }, { merge: true });

        // Clear completed subcourses
        await setDoc(doc(db, 'users', user.uid, 'completedSubCourses', course.id), {
          completed: {},
          firstCompletedAt: new Date().toISOString()
        }, { merge: true });

        // Delete prior exam results for this course to reset attempts
        const resultsRef = collection(db, 'users', user.uid, 'examResults');
        const resultsSnap = await getDocs(resultsRef);
        const deletions = [];
        resultsSnap.forEach((r) => {
          const d = r.data();
          if (d && d.courseId === course.id) {
            deletions.push(deleteDoc(doc(db, 'users', user.uid, 'examResults', r.id)));
          }
        });
        await Promise.all(deletions);

        setHasFailed(false);
        setIsEnrolled(true);
        setCourseProgress(0);
        setShowRetakeModal(false);
        navigate(`/courses/${course.id}`);
      } catch (e) {
        console.error('Error resetting course for retake:', e);
        alert('Failed to reset course for retake. Please try again.');
      } finally {
        setLoading(false);
      }
    };


    const handleModalClose = () => {
      setShowModal(false);
    };

    if (loading) {
      return <LoadingIndicator />;
    }

    return (
      <div className={`bg-white rounded-lg shadow-lg overflow-hidden relative p-4 w-full md:w-72 lg:w-80 h-auto ${course.status === 'coming_soon' ? 'grayscale' : ''}`}>
        {course.status === 'coming_soon' && (
          <div className="absolute top-4 right-4 bg-gray-800 text-white px-3 py-1 rounded-full z-20">
            Coming Soon
          </div>
        )}
        <img src="/images/rcourses.png" alt={course.title} className="w-full h-32 object-cover" />
        
        {(isEnrolled || hasFailed) && (
          <div className="absolute top-4 right-4 flex items-center bg-white rounded-full p-1 shadow">
            <div className="relative w-12 h-12">
              <svg className="w-12 h-12 transform -rotate-90">
                <circle
                  className="text-gray-200"
                  strokeWidth="4"
                  stroke="currentColor"
                  fill="transparent"
                  r="20"
                  cx="24"
                  cy="24"
                />
                <circle
                  className="text-[rgb(130,88,18)]"
                  strokeWidth="4"
                  strokeLinecap="round"
                  stroke="currentColor"
                  fill="transparent"
                  r="20"
                  cx="24"
                  cy="24"
                  strokeDasharray={`${courseProgress * 1.256} 126`}
                />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold">
                {hasFailed ? 'Failed' : `${Math.round(courseProgress)}%`}
              </span>
            </div>
          </div>
        )}

        <div className="p-2">
          <h3 className="text-lg font-bold text-gray-800">{course.title}</h3>
          <p className="text-gray-600">{course.description}</p>

          <div className="flex justify-between mt-2">
            {course.status === 'coming_soon' ? (
              <button
                disabled
                className="bg-gray-400 text-white px-4 py-2 rounded-lg cursor-not-allowed w-full"
              >
                Coming Soon
              </button>
            ) : (
              <>
                <button
                  onClick={handleLearnClick}
                  className="bg-[rgb(130,88,18)] text-white px-4 py-2 rounded-lg hover:bg-[rgb(110,68,0)] transition-colors"
                >
                  {hasFailed ? 'Retake' : (isEnrolled ? (courseProgress === 100 ? 'Review' : 'Continue') : 'Enroll')}
                </button>
                <button
                  onClick={toggleDetails}
                  className="bg-[rgb(130,88,18)] text-white px-4 py-2 rounded-lg hover:bg-[rgb(110,68,0)] transition-colors"
                >
                  {courseProgress === 100 ? "Download Certificate" : (showDetails ? "Hide Details" : "Show Details")}
                </button>
              </>
            )}
          </div>

          {showDetails && courseProgress !== 100 && (
            <div className="mt-4 text-sm text-gray-700">
              <p><strong>Difficulty:</strong> {course.difficulty}</p>
              <p><strong>Prerequisites:</strong> {course.prerequisites}</p>
              <p><strong>Learning Objectives:</strong> {course.learningObjectives}</p>
              <p><strong>Target Audience:</strong> {course.targetAudience}</p>
              {isEnrolled && (
                <p className="mt-2">
                  <strong>Status:</strong>{' '}
                  {courseProgress === 100 ? 'Completed' : 
                   courseProgress > 0 ? 'In Progress' : 'Not Started'}
                </p>
              )}
            </div>
          )}
        </div>

        {showModal && ReactDOM.createPortal(
          <Modal onClose={handleModalClose}>
            <div className="text-center">
              <h2 className="text-xl font-bold mb-4">Enroll in {course.title}</h2>
              <p className="mb-4">Would you like to enroll in this course?</p>
              <div className="flex justify-center gap-3">
                <button 
                  onClick={handleEnroll}
                  disabled={loading}
                  className="bg-[rgb(130,88,18)] text-white px-6 py-2 rounded-lg hover:bg-[rgb(110,68,0)] disabled:opacity-50 disabled:cursor-not-allowed active:bg-[rgb(90,48,0)]"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Enrolling...
                    </span>
                  ) : (
                    'Enroll Now'
                  )}
                </button>
                <button 
                  onClick={handleModalClose}
                  className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 active:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>,
          document.body
        )}

        {showRetakeModal && ReactDOM.createPortal(
          <Modal onClose={() => setShowRetakeModal(false)}>
            <div className="text-center">
              <h2 className="text-xl font-bold mb-4">Retake {course.title}</h2>
              <p className="mb-4">This will reset your progress and attempts for this course, allowing you to start from the beginning. Continue?</p>
              <div className="flex justify-center gap-3">
                <button 
                  onClick={handleRetake}
                  disabled={loading}
                  className="bg-[rgb(130,88,18)] text-white px-6 py-2 rounded-lg hover:bg-[rgb(110,68,0)] disabled:opacity-50 disabled:cursor-not-allowed active:bg-[rgb(90,48,0)]"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Resetting...
                    </span>
                  ) : (
                    'Confirm Retake'
                  )}
                </button>
                <button 
                  onClick={() => setShowRetakeModal(false)}
                  className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 active:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>,
          document.body
        )}
      </div>
    );
  };

  const CategoryPane = ({ title, coursesByCategory, paneType }) => {
    const toggleCategory = (category) => {
      setActiveCategory(prev => ({
        ...prev,
        [paneType]: prev[paneType] === category ? null : category
      }));
    };

    return (
      <section className="flex flex-col md:flex-row mb-16">
        <aside className="w-full md:w-1/4 p-4 bg-gray-100">
          <h2 className="text-2xl font-bold mb-4">{title} Categories</h2>
          <div className="space-y-2">
            {allCategories.map(category => (
              <div
                key={category}
                className={`p-2 cursor-pointer ${activeCategory[paneType] === category ? 'bg-gray-300' : 'bg-gray-200'} rounded-lg`}
                onClick={() => toggleCategory(category)}
              >
                {category} ({(coursesByCategory[category] || []).length})
              </div>
            ))}
          </div>
        </aside>

        <main className="flex-1 p-4">
          {allCategories.map(category => (
            activeCategory[paneType] === category && (
              <section
                key={category}
                className="transition-all duration-500 ease-in-out"
              >
                <h3 className="text-3xl font-bold mb-4">{category} Courses</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(coursesByCategory[category] || []).map(course => (
                    <CourseCard key={course.id} course={course} />
                  ))}
                </div>
              </section>
            )
          ))}
        </main>
      </section>
    );
  };

  const handleTabChange = (index) => {
    setActiveTab(index);
  };

  if (loading) {
    return <LoadingIndicator />;
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-center mb-8">
        <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
          <button
            onClick={() => handleTabChange(0)}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              activeTab === 0
                ? 'bg-white text-[rgb(130,88,18)] shadow'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Introduction
          </button>
          <button
            onClick={() => handleTabChange(1)}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              activeTab === 1
                ? 'bg-white text-[rgb(130,88,18)] shadow'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Certification Courses
          </button>
          <button
            onClick={() => handleTabChange(2)}
            className={`px-4 py-2 rounded-md text-sm font-medium ${
              activeTab === 2
                ? 'bg-white text-[rgb(130,88,18)] shadow'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Recommended Courses
          </button>
        </div>
      </div>

      {activeTab === 0 && <CourseIntroduction />}
      {activeTab === 1 && (
        <CategoryPane
          title="Certification Courses"
          coursesByCategory={certificationCourses}
          paneType="certification"
        />
      )}
      {activeTab === 2 && (
        <CategoryPane
          title="Recommended Courses"
          coursesByCategory={recommendedCourses}
          paneType="recommended"
        />
      )}
    </div>
  );
}

const Modal = ({ children, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg p-6 shadow-xl w-full max-w-md relative">
        {children}
      </div>
    </div>
  );
};

export default CoursePage;